import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEdit, FaTrash, FaAngleLeft, FaAngleRight, FaPlus, FaSearch } from 'react-icons/fa';
import { toast, Toaster } from 'react-hot-toast';

// CSS animation definitions
const globalStyles = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @keyframes fadeIn {
    0% { opacity: 0; }
    100% { opacity: 1; }
  }
`;

export default function ProductList() {
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [confirmDelete, setConfirmDelete] = useState({ open: false, productId: null });
const [deleting, setDeleting] = useState(false);

    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        totalPages: 1,
        totalProducts: 0
    });
    const [selectedCategory, setSelectedCategory] = useState('All Categories');
    const [selectedStatus, setSelectedStatus] = useState('All Status');

    // Check screen size on resize
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
            setIsSmallMobile(window.innerWidth <= 480);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch products from API
    const fetchProducts = async () => {
        try {
            setLoading(true);
            let url = `https://api.alburaqscents.com/v1/product?page=${pagination.page}&limit=${pagination.limit}`;
            
            if (searchTerm) url += `&search=${searchTerm}`;
            if (selectedCategory !== 'All Categories') url += `&category=${selectedCategory}`;
            if (selectedStatus !== 'All Status') url += `&status=${selectedStatus.toLowerCase()}`;

            const response = await fetch(url);
            const result = await response.json();

            if (result.success) {
                const processedProducts = result.data.map(product => {
                    const largestSize = product.sizes.reduce((max, size) => 
                        size.ml > max.ml ? size : max, 
                        {ml: 0, price: 0}
                    );
                    const totalStock = product.sizes.reduce((sum, size) => sum + size.stock, 0);
                    
                    return {
                        ...product,
                        displayPrice: largestSize.price,
                        totalStock,
                        statusDisplay: product.status === 'active' 
                            ? (totalStock > 0 ? 'active' : 'out of stock')
                            : 'inactive'
                    };
                });

                setProducts(processedProducts);
                setPagination({
                    page: result.page,
                    limit: result.limit,
                    totalPages: result.totalPages,
                    totalProducts: result.totalProducts
                });
            } else {
                toast.error(result.message || 'Failed to load products');
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            toast.error('Network error while loading products');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, [pagination.page, searchTerm, selectedCategory, selectedStatus]);

    const handleViewProduct = (productId) => {
        navigate(`/products/details/${productId}`);
    };

    const handleEditProduct = (productId) => {
        navigate(`/products/edit/${productId}`);
    };

 const handleDeleteProduct = async (productId) => {
    setConfirmDelete({ open: true, productId });
};

const confirmDeletion = async () => {
    const productId = confirmDelete.productId;
    setDeleting(true); // Start spinner
    try {
        const response = await fetch(`https://api.alburaqscents.com/v1/product/${productId}`, {
            method: 'DELETE'
        });
        const result = await response.json();

        if (result.success) {
            toast.success('Product deleted successfully');
            fetchProducts();
        } else {
            toast.error(result.message || 'Failed to delete product');
        }
    } catch (error) {
        toast.error('Network error while deleting product');
    } finally {
        setDeleting(false); // Stop spinner
        setConfirmDelete({ open: false, productId: null });
    }
};



    const handleAddProduct = () => {
        navigate('/products/add');
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, page: newPage }));
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleCategoryChange = (e) => {
        setSelectedCategory(e.target.value);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleStatusChange = (e) => {
        setSelectedStatus(e.target.value);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const renderPaginationButtons = () => {
        const buttons = [];
        const maxVisibleButtons = 5;
        let startPage = 1;
        let endPage = pagination.totalPages;

        if (pagination.totalPages > maxVisibleButtons) {
            const half = Math.floor(maxVisibleButtons / 2);
            if (pagination.page <= half) {
                endPage = maxVisibleButtons;
            } else if (pagination.page + half >= pagination.totalPages) {
                startPage = pagination.totalPages - maxVisibleButtons + 1;
            } else {
                startPage = pagination.page - half;
                endPage = pagination.page + half;
            }
        }

        if (startPage > 1) {
            buttons.push(
                <button
                    key={1}
                    style={styles.paginationButton}
                    onClick={() => handlePageChange(1)}
                >
                    1
                </button>
            );
            if (startPage > 2) {
                buttons.push(<span key="start-ellipsis" style={styles.paginationText}>...</span>);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            buttons.push(
                <button
                    key={i}
                    style={{
                        ...styles.paginationButton,
                        ...(i === pagination.page && styles.paginationButtonActive)
                    }}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </button>
            );
        }

        if (endPage < pagination.totalPages) {
            if (endPage < pagination.totalPages - 1) {
                buttons.push(<span key="end-ellipsis" style={styles.paginationText}>...</span>);
            }
            buttons.push(
                <button
                    key={pagination.totalPages}
                    style={styles.paginationButton}
                    onClick={() => handlePageChange(pagination.totalPages)}
                >
                    {pagination.totalPages}
                </button>
            );
        }

        return buttons;
    };

    // Styles object
    const styles = {
        mainWrapper: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            minHeight: '100vh',
            width: '100%',
            backgroundColor: 'transparent',
            padding: isSmallMobile ? '0px' : (isMobile ? '15px' : '20px'),
            boxSizing: 'border-box',
            fontFamily: 'Inter, sans-serif',
            overflowX: 'hidden',
            animation: 'fadeIn 0.5s ease-in-out'
        },
        productListPageContainer: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            maxWidth: '1200px',
            backgroundColor: 'transparent',
            padding: isSmallMobile ? '15px' : '20px',
            boxSizing: 'border-box'
        },
        headerSection: {
            padding: '0',
            marginBottom: '30px',
            boxSizing: 'border-box',
            width: '100%'
        },
        pageTitle: {
            fontSize: isSmallMobile ? '24px' : (isMobile ? '28px' : '32px'),
            fontWeight: 'bold',
            color: '#000',
            marginBottom: '5px',
            textAlign: 'left'
        },
        breadcrumbs: {
            fontSize: isSmallMobile ? '12px' : '14px',
            color: '#555',
            marginBottom: '10px',
            textAlign: 'left'
        },
        productListContainer: {
            backgroundColor: '#000',
            borderRadius: '10px',
            padding: isSmallMobile ? '15px' : '20px',
            color: '#E5D0A5',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column'
        },
        productListHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '15px',
            flexDirection: isSmallMobile ? 'column' : 'row',
            gap: isSmallMobile ? '10px' : '0',
            textAlign: isSmallMobile ? 'center' : 'left'
        },
        searchBar: {
            flexGrow: 1,
            minWidth: '200px',
            maxWidth: '400px',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'transparent',
            borderRadius: '24px',
            border: '1px solid #D4AF37',
            padding: '10px 20px',
            height: '50px'
        },
        searchIcon: {
            color: '#D4AF37',
            fontSize: '17px',
            marginRight: '10px'
        },
        searchInput: {
            backgroundColor: 'transparent',
            border: 'none',
            outline: 'none',
            color: '#D4AF37',
            fontSize: '17px',
            flexGrow: 1,
            width: '100%'
        },
        searchFilterContainer: {
            display: 'flex',
            gap: '10px',
            flexWrap: 'wrap',
            justifyContent: isSmallMobile ? 'center' : 'flex-end',
            width: isSmallMobile ? '100%' : 'auto'
        },
        selectFilter: {
            width: '120px',
            backgroundColor: '#333',
            color: '#E5D0A5',
            border: '1px solid #555',
            borderRadius: '5px',
            padding: '8px 10px',
            fontSize: '12px',
            outline: 'none',
            appearance: 'none',
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Cpath fill='%23E5D0A5' d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 8px center',
            backgroundSize: '12px',
            cursor: 'pointer'
        },
        addButton: {
            backgroundColor: '#D4AF37',
            color: '#000',
            border: 'none',
            borderRadius: '5px',
            padding: '8px 15px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: 'bold',
            transition: 'background-color 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            '&:hover': {
                backgroundColor: '#c9a227'
            }
        },
        tableWrapper: {
            overflowX: 'auto',
            marginBottom: '15px',
            flexGrow: 1,
            '&::-webkit-scrollbar': {
                width: '8px',
                height: '8px'
            },
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#555',
                borderRadius: '4px'
            },
            '&::-webkit-scrollbar-track': {
                backgroundColor: '#333'
            }
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            minWidth: isMobile ? '700px' : '900px'
        },
        tableHeader: {
            backgroundColor: '#000',
            color: '#E5D0A5',
            fontSize: '12px',
            textAlign: 'left',
            padding: '8px 0',
            borderBottom: '1px solid #333'
        },
        th: {
            padding: '16px 10px',
            whiteSpace: 'nowrap'
        },
        tableRow: {
            borderBottom: '1px solid #333',
            '&:hover': {
                backgroundColor: '#1a1a1a'
            }
        },
        td: {
            padding: '16px 10px',
            verticalAlign: 'middle',
            whiteSpace: 'nowrap',
            fontSize: '12px'
        },
        statusPill: {
            display: 'inline-block',
            padding: '4px 8px',
            borderRadius: '5px',
            fontSize: '10px',
            fontWeight: 'bold',
            textAlign: 'center'
        },
        statusActive: {
            backgroundColor: '#8BC34A',
            color: '#000'
        },
        statusInactive: {
            backgroundColor: '#F44336',
            color: '#FFF'
        },
        outOfStock: {
            backgroundColor: '#FF9800',
            color: '#000'
        },
        actionButtons: {
            display: 'flex',
            gap: '4px',
            justifyContent: 'center'
        },
        actionButton: {
            backgroundColor: '#E5D0A5',
            border: 'none',
            borderRadius: '5px',
            padding: '5px 7px',
            cursor: 'pointer',
            fontSize: '12px',
            color: '#000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.3s ease',
            '&:hover': {
                backgroundColor: '#d0b98e'
            }
        },
        actionButtonDanger: {
            backgroundColor: '#F44336',
            color: '#FFF',
            '&:hover': {
                backgroundColor: '#d32f2f'
            }
        },
        actionButtonIcon: {
            fontSize: '13px'
        },
        paginationContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '15px',
            flexDirection: isSmallMobile ? 'column' : 'row',
            gap: isSmallMobile ? '10px' : '0',
            padding: '0 5px'
        },
        paginationControls: {
            display: 'flex',
            gap: '4px',
            flexWrap: 'wrap',
            justifyContent: isSmallMobile ? 'center' : 'flex-start'
        },
        paginationButton: {
            backgroundColor: '#E5D0A5',
            color: '#000',
            border: '1px solid #000',
            borderRadius: '5px',
            padding: '4px 8px',
            cursor: 'pointer',
            fontSize: '12px',
            transition: 'background-color 0.3s ease',
            minWidth: '25px',
            textAlign: 'center',
            '&:hover': {
                backgroundColor: '#d0b98e'
            }
        },
        paginationButtonActive: {
            backgroundColor: '#000',
            color: '#E5D0A5',
            '&:hover': {
                backgroundColor: '#000'
            }
        },
        paginationText: {
            color: '#E5D0A5',
            fontSize: '12px',
            marginTop: isSmallMobile ? '8px' : '0'
        },
        loadingContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
            width: '100%'
        },
        loadingSpinner: {
            border: '4px solid rgba(0, 0, 0, 0.1)',
            borderTop: '4px solid #E5D0A5',
            borderRadius: '50%',
            width: '30px',
            height: '30px',
            animation: 'spin 1s linear infinite'
        },
        emptyState: {
            textAlign: 'center',
            padding: '40px',
            color: '#E5D0A5',
            animation: 'fadeIn 0.5s ease-in-out'
        }
    };

    return (
        <div style={styles.mainWrapper}>
            <style>{globalStyles}</style>
            <Toaster position="top-right" />
            
            <div style={styles.productListPageContainer}>
                <div style={styles.headerSection}>
                    <h1 style={styles.pageTitle}>Products</h1>
                    <p style={styles.breadcrumbs}>Home • Products • Product List</p>
                </div>

                <div style={styles.productListContainer}>
                    <div style={styles.productListHeader}>
                        <div style={styles.searchBar}>
                            <FaSearch style={styles.searchIcon} />
                            <input
                                type="text"
                                style={styles.searchInput}
                                placeholder="Search products..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                            />
                        </div>
                        <div style={styles.searchFilterContainer}>
                            <select 
                                style={styles.selectFilter}
                                value={selectedCategory}
                                onChange={handleCategoryChange}
                            >
                                <option>All Categories</option>
                                <option>Electronics</option>
                                <option>Apparel</option>
                                <option>Groceries</option>
                                <option>Fitness</option>
                                <option>Home</option>
                                <option>Stationery</option>
                            </select>
                            <select 
                                style={styles.selectFilter}
                                value={selectedStatus}
                                onChange={handleStatusChange}
                            >
                                <option>All Status</option>
                                <option>Active</option>
                                <option>Inactive</option>
                                <option>Out of Stock</option>
                            </select>
                            <button
                                style={styles.addButton}
                                onClick={handleAddProduct}
                            >
                                <FaPlus /> Add Product
                            </button>
                        </div>
                    </div>

                    <div style={styles.tableWrapper}>
                        {loading ? (
                            <div style={styles.loadingContainer}>
                                <div style={styles.loadingSpinner}></div>
                            </div>
                        ) : products.length === 0 ? (
                            <div style={styles.emptyState}>No products found</div>
                        ) : (
                            <table style={styles.table}>
                                <thead style={styles.tableHeader}>
                                    <tr>
                                        <th style={styles.th}>ID</th>
                                        <th style={styles.th}>Product Name</th>
                                        <th style={styles.th}>Category</th>
                                        <th style={styles.th}>SKU</th>
                                        <th style={styles.th}>QTY</th>
                                        <th style={styles.th}>Price</th>
                                        <th style={styles.th}>Status</th>
                                        <th style={styles.th}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {products.map((product) => (
                                        <tr key={product._id} style={styles.tableRow}>
                                            <td style={styles.td}>#{product._id.substring(18)}</td>
                                            <td style={styles.td}>{product.name}</td>
                                            <td style={styles.td}>{product.categoryId?.name}</td>
                                            <td style={styles.td}>{product.sku}</td>
                                            <td style={styles.td}>{product.totalStock}</td>
                                            <td style={styles.td}>${product.displayPrice.toFixed(2)}</td>
                                            <td style={styles.td}>
                                                <span style={{
                                                    ...styles.statusPill,
                                                    ...(product.statusDisplay === 'active' 
                                                        ? styles.statusActive 
                                                        : product.statusDisplay === 'inactive' 
                                                            ? styles.statusInactive 
                                                            : styles.outOfStock)
                                                }}>
                                                    {product.statusDisplay === 'active' ? 'Active' : 
                                                     product.statusDisplay === 'inactive' ? 'Inactive' : 'Out of Stock'}
                                                </span>
                                            </td>
                                            <td style={styles.td}>
                                                <div style={styles.actionButtons}>
                                                    <button
                                                        style={styles.actionButton}
                                                        onClick={() => handleViewProduct(product._id)}
                                                    >
                                                        <FaEye style={styles.actionButtonIcon} />
                                                    </button>
                                                    <button
                                                        style={styles.actionButton}
                                                        onClick={() => handleEditProduct(product._id)}
                                                    >
                                                        <FaEdit style={styles.actionButtonIcon} />
                                                    </button>
                                                    <button
                                                    disabled
                                                        style={{
                                                            ...styles.actionButton,
                                                            ...styles.actionButtonDanger
                                                        }}
                                                        onClick={() => handleDeleteProduct(product._id)}
                                                    >
                                                        <FaTrash style={styles.actionButtonIcon} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    <div style={styles.paginationContainer}>
                        <div style={styles.paginationControls}>
                            <button
                                style={{
                                    ...styles.paginationButton,
                                    ...(pagination.page === 1 && { opacity: 0.5, cursor: 'not-allowed' })
                                }}
                                onClick={() => handlePageChange(pagination.page - 1)}
                                disabled={pagination.page === 1}
                            >
                                <FaAngleLeft />
                            </button>
                            
                            {renderPaginationButtons()}

                            <button
                                style={{
                                    ...styles.paginationButton,
                                    ...(pagination.page === pagination.totalPages && { opacity: 0.5, cursor: 'not-allowed' })
                                }}
                                onClick={() => handlePageChange(pagination.page + 1)}
                                disabled={pagination.page === pagination.totalPages}
                            >
                                <FaAngleRight />
                            </button>
                        </div>
                        <p style={styles.paginationText}>
                            Showing {(pagination.page - 1) * pagination.limit + 1}-
                            {Math.min(pagination.page * pagination.limit, pagination.totalProducts)} 
                            of {pagination.totalProducts} products
                        </p>
                    </div>
                </div>
            </div>
            {confirmDelete.open && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    backdropFilter: 'blur(2px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
    animation: 'fadeIn 0.3s ease-in-out',
    fontFamily: 'Arial, sans-serif'
  }}>
    <div style={{
      backgroundColor: '#000',
      padding: '30px 25px',
      borderRadius: '10px',
      width: '90%',
      maxWidth: '400px',
      color: '#E5D0A5',
      textAlign: 'center',
      border: '1px solid #D4AF37',
      boxShadow: '0 0 20px rgba(212, 175, 55, 0.5)'
    }}>
      <h3 style={{ fontSize: '17px', marginBottom: '15px', color: '#D4AF37' }}>Confirm Deletion</h3>
      <p style={{ fontSize: '14px', marginBottom: '25px', color: '#E5D0A5' }}>
        Are you sure you want to delete this product?
      </p>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', flexWrap: 'wrap' }}>
        <button
  onClick={confirmDeletion}
  disabled={deleting}
  style={{
    backgroundColor: deleting ? '#aaa' : '#F44336',
    color: '#fff',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '5px',
    cursor: deleting ? 'not-allowed' : 'pointer',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  }}
>
  {deleting ? (
    <span
      style={{
        width: '16px',
        height: '16px',
        border: '2px solid #fff',
        borderTop: '2px solid transparent',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }}
    />
  ) : (
    'Yes, Delete'
  )}
</button>

        <button
          onClick={() => setConfirmDelete({ open: false, productId: null })}
          style={{
            backgroundColor: '#D4AF37',
            color: '#000',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '5px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}

        </div>
    );
}